import React, { FormEvent, useEffect, useState } from 'react';
import { Autocomplete, Button, MenuItem, styled, TextField } from '@mui/material';
import { Search } from '@mui/icons-material';
import FilterBox from 'components/filter-box/FilterBox';
import KeyboardDatePicker from 'components/pickers/KeyboardDatePicker';
import { api } from 'services/api';
import { User } from 'types/user';
import { ReportGuideQueryParams } from './hooks/useFetchReportGuides';

const Filter = styled('form')(({ theme }) => ({
  display: 'grid',
  alignItems: 'center',
  gridTemplateColumns: '160px 180px 180px repeat(3, minmax(170px, 1fr)) 120px',
  gap: theme.spacing(1.5),
  [theme.breakpoints.down('lg')]: {
    gridTemplateColumns: 'repeat(4, 1fr)',
  },
  [theme.breakpoints.down('md')]: {
    gridTemplateColumns: '1fr 1fr',
  },
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: '1fr',
  },
}));

type ReportGuideFilterBoxProps = {
  filter: ReportGuideQueryParams;
  handleChange<K extends keyof ReportGuideQueryParams>(index: K, value: ReportGuideQueryParams[K]): void;
  handleSearch(e?: FormEvent<HTMLFormElement>): void;
  loading: boolean;
};

const ReportGuideFilterBox: React.FC<ReportGuideFilterBoxProps> = ({ filter, loading, handleChange, handleSearch }) => {
  const [users, setUsers] = useState<User[]>([]);
  const dateLabel = filter.date_filter === 'appointment_date' ? 'Consulta' : 'Emissão';

  useEffect(() => {
    api
      .get<User[]>('/users/all', { params: { name: '' } })
      .then(response => setUsers(response.data))
      .catch(() => setUsers([]));
  }, []);

  return (
    <FilterBox>
      <Filter onSubmit={handleSearch}>
        <TextField
          select
          label="Pesquisar por"
          value={filter.date_filter}
          onChange={e => handleChange('date_filter', e.target.value as ReportGuideQueryParams['date_filter'])}
        >
          <MenuItem value="created_at">Emissão</MenuItem>
          <MenuItem value="appointment_date">Consulta</MenuItem>
        </TextField>
        <KeyboardDatePicker
          label={`${dateLabel} inicial`}
          value={filter.initial_date}
          onChange={date => handleChange('initial_date', date)}
        />
        <KeyboardDatePicker
          label={`${dateLabel} final`}
          value={filter.final_date}
          onChange={date => handleChange('final_date', date)}
        />
        <TextField
          label="Paciente"
          value={filter.patient_name}
          onChange={e => handleChange('patient_name', e.target.value)}
        />
        <TextField
          label="Médico"
          value={filter.doctor_name}
          onChange={e => handleChange('doctor_name', e.target.value)}
        />
        <Autocomplete
          value={users.find(u => u.id === filter.marker_id) || null}
          options={users}
          noOptionsText="Nenhum marcador encontrado"
          getOptionLabel={option => option.name}
          onChange={(_, user) => handleChange('marker_id', user ? user.id : null)}
          renderInput={params => <TextField {...params} label="Marcador" />}
        />

        <Button
          className="search-button"
          type="submit"
          size="small"
          disabled={loading}
          variant="contained"
          color="primary"
          startIcon={<Search fontSize="small" />}
        >
          Buscar
        </Button>
      </Filter>
    </FilterBox>
  );
};

export default ReportGuideFilterBox;
